import { useState, useEffect, useCallback, useMemo } from "react";

import MainCard from "components/MainCard";
import { DataGrid } from "@mui/x-data-grid";

import DialogDetail from "./DialogDetail";
import DialogVerifikasi from "./DialogVerifikasi";
import { getColumns } from "./columns";

import {
  getAktivitasBawahan,
  setujuiAktivitas,
  revisiAktivitas
} from "./api";

import {
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  Box,
  Button
} from "@mui/material";

import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";

export default function VerifikasiAktivitas() {

  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [pegawaiFilter, setPegawaiFilter] = useState("ALL");
  const [tanggalFilter, setTanggalFilter] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  /* dialog verifikasi */

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  /* ================= LOAD DATA ================= */

  const fetchData = useCallback(async () => {

    try {

      setLoading(true);

      const data = await getAktivitasBawahan(token);
      console.log("DATA API:", data);

      const mapped = (data || []).map((item, index) => ({

        id: item.id,
        no: index + 1,

        tanggal: item.tanggal
          ? new Date(item.tanggal).toLocaleDateString("id-ID")
          : "-",

        nip: item.nip ?? "-",

        nama: item.nama ?? "-",
        aktivitas: item.nama_kegiatan ?? "-",

        kategori: item.kategori ?? "-",
        lokasi: item.lokasi ?? "-",

        durasi: `${item.durasi ?? 0} Jam`,
        status: String(item.status ?? "")
          .toLowerCase()
          .trim(),

        deskripsi: item.deskripsi ?? "",
        link_bukti: item.link_bukti ?? ""

      }));

      setRows(mapped);

    } catch (err) {

      console.error("FETCH ERROR:", err);

    } finally {

      setLoading(false);

    }

  }, [token]);

  // Reload Setiap 60 detik
  useEffect(() => {

    fetchData(); // pertama load

    const interval = setInterval(() => {
      console.log("AUTO REFRESH...");
      fetchData();
    }, 60000); 

    return () => clearInterval(interval);

  }, [fetchData]);

  /* ================= ACTION ================= */

  const handleSetujui = (id) => {

    setSelectedId(id);
    setDialogType("setujui");
    setDialogOpen(true);

  };

  const handleRevisi = (id) => {

    setSelectedId(id);
    setDialogType("revisi");
    setDialogOpen(true);

  };

const handleSubmitDialog = async (catatan) => {
  try {

    if (!selectedId) {
      console.error("ID kosong!");
      return;
    }

    if (dialogType === "setujui") {

      await setujuiAktivitas(selectedId, {
        catatan
      });

    } else {

      await revisiAktivitas(selectedId, {
        catatan_revisi: catatan 
      });

    }

    setDialogOpen(false);
    fetchData();

  } catch (err) {

    console.error("VERIFIKASI ERROR:", err);

  }
};

  const openDetail = (row) => {

    setSelectedActivity(row);
    setDetailOpen(true);

  };

  /* ================= FILTER LIST ================= */

  const pegawaiList = useMemo(() => {

    return [...new Set(rows.map((r) => r.nama))];

  }, [rows]);

  /* ================= FILTER ================= */

  const filteredRows = useMemo(() => {

    const q = (search || "").toLowerCase();

    return rows.filter((row) => {

      const nama = (row.nama || "").toLowerCase();
      const aktivitas = (row.aktivitas || "").toLowerCase();
      const kategori = (row.kategori || "").toLowerCase();

      const matchSearch =
        nama.includes(q) ||
        aktivitas.includes(q) ||
        kategori.includes(q);

      const matchStatus =
        statusFilter === "ALL" ||
        row.status === statusFilter;

      const matchPegawai =
        pegawaiFilter === "ALL" ||
        row.nama === pegawaiFilter;

      const matchTanggal =
        !tanggalFilter ||
        row.tanggal ===
          new Date(tanggalFilter).toLocaleDateString("id-ID");

      return (
        matchSearch &&
        matchStatus &&
        matchPegawai &&
        matchTanggal
      );

    });

  }, [rows, search, statusFilter, pegawaiFilter, tanggalFilter]);


  const handleResetFilter = () => {
    setSearch("");
    setStatusFilter("ALL");
    setPegawaiFilter("ALL");
    setTanggalFilter("");
  };

  /* ================= TABLE ================= */

  const columns = getColumns(
    openDetail,
    handleSetujui,
    handleRevisi
  );

  /* ================= RENDER ================= */

  return (

    <MainCard
      title="Verifikasi Aktivitas Bawahan"
      secondary={

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >

            {/* SEARCH */}
            <TextField
              size="small"
              placeholder="Cari aktivitas..."
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              sx={{
                minWidth: 230,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2
                }
              }}
              InputProps={{
                startAdornment:(
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                )
              }}
            />

            {/* STATUS */}
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e)=>setStatusFilter(e.target.value)}
              sx={{
                minWidth: 170,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2
                }
              }}
            >
              <MenuItem value="ALL">
                Semua Status
              </MenuItem>

              <MenuItem value="dikirim">
                Dikirim
              </MenuItem>
            </TextField>

            {/* PEGAWAI */}
            <TextField
              select
              size="small"
              value={pegawaiFilter}
              onChange={(e)=>setPegawaiFilter(e.target.value)}
              sx={{
                minWidth: 180,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2
                }
              }}
            >
              <MenuItem value="ALL">
                Semua Pegawai
              </MenuItem>

              {pegawaiList.map((nama)=>(
                <MenuItem
                  key={nama}
                  value={nama}
                >
                  {nama}
                </MenuItem>
              ))}
            </TextField>

            {/* TANGGAL */}
            <TextField
              type="date"
              size="small"
              value={tanggalFilter}
              onChange={(e)=>setTanggalFilter(e.target.value)}
              sx={{
                minWidth: 165,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2
                }
              }}
            />

            {/* RESET */}
            <Button
              variant="outlined"
              size="medium"
              startIcon={<ReloadOutlined />}
              onClick={handleResetFilter}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                px: 2
              }}
            >
              Reset Filter
            </Button>

          </Stack>


      }
    >
      <Box sx={{ height:540 }}>

        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[5,10,20,50]}
          initialState={{
            pagination:{
              paginationModel:{ pageSize:10 }
            }
          }}
        />

      </Box>

      <DialogDetail
        open={detailOpen}
        onClose={()=>setDetailOpen(false)}
        activity={selectedActivity}
      />

      <DialogVerifikasi
        open={dialogOpen}
 
        type={dialogType}
        onClose={()=>setDialogOpen(false)}
        onSubmit={handleSubmitDialog}
      />

    </MainCard>

  );

}